import React, { Component, ReactNode } from 'react';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import LanguageUtil from '../../util/LanguageUtil';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import ItemStatusComponent from '../ItemStatusComponent';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import { AiModelState } from '../../redux/reducers/AiModelReducer';
import AiModelService from '../../service/AiModelService';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import Order = query.Order;
import { MLOpsModel } from '../../domain/mlops/MLOpsDomains';

class AiModelListViewerComponent extends Component<Props> {
  private aiModelService: AiModelService = ServiceContainer.getInstance().get(ServiceNames.AI_MODEL);

  public render(): ReactNode {
    const {
      views,
      aiModels,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && aiModels && views.length > 0 && aiModels.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full  rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={aiModels}
          columns={this.getColumns()}
          selectionMode={'checkbox'}
          selection={selectedComponentIds}
          onSelectionChange={this.onSelectionChange}
          onRowClick={this.openEditor}
          onSortChange={onSortChange}
          componentName={ComponentName.AI_MODEL}
          openNewTab={true}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
        />
        <ListDraggableItem itemType={ComponentName.AI_MODEL} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent createDisabled={false} type={ComponentName.AI_MODEL} />
    );
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.ai.table.name', defaultMessage: 'Name' }),
        className: 'min-w-[20%]',
        copyable: true,
        onCopy: (e: MLOpsModel) => {
          return e.name;
        },
        body: (record: MLOpsModel) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          if (view == undefined) return null;
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={35} text={record.name} />
            </div>
          );
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: 'pages.ai.table.status', defaultMessage: 'Status' }),
        className: 'min-w-[20%]',
        body: (item: MLOpsModel) => {
          return <ItemStatusComponent status={item.stage || 'DRAFT'} />;
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.ai.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[20%]',
        body: (record: MLOpsModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 justify-start relative group'}>
              <CustomTableContentWrapper clipSize={35} text={record.description || LanguageUtil.get({ id: 'common.label.noDescription', defaultMessage: 'No description' })} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.ai.table.createdAt', defaultMessage: 'Created At' }),
        className: 'min-w-[15%]',
        body: (record: MLOpsModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'pages.ai.table.updatedAt', defaultMessage: 'Updated At' }),
        className: 'min-w-[15%]',
        body: (record: MLOpsModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit(ComponentName.AI_MODEL)) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.ai.table.actions', defaultMessage: 'Actions' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: MLOpsModel) => {
    const { permissions } = this.props;

    const actions: ReactNode[] = [];

    const permission = permissions[record.name];

    const allowed = ['Edit', 'Admin'];
    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.AI_MODEL, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => confirmUndeployWithImpact(ComponentName.AI_MODEL, [record.name], () => this.aiModelService.undeploy(record)),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.aiModelService.remove(record),
              message: LanguageUtil.get({ id: 'messages.confirm.confirmRemove', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.aiModelService.deploy(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );

    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openAiModel } = this.props;
    if (e.data) {
      openAiModel(e.data.name);
    }
  };
}

const mapStateToProps = (state: RootState) => ({
  aiModel: state.aiModel as AiModelState,
  component: state.component as ComponentState,
});

const mapDispatchToProps = {};

interface Props extends ReduxProps {
  views: any;
  aiModels: MLOpsModel[];
  openAiModel: (id: string) => void;
  currentModal: any;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(AiModelListViewerComponent);
